/**
 * LocalStorage utility wrapper for Nuxt
 * Provides safe access to localStorage with SSR compatibility
 */

/**
 * Check if localStorage is available (client-side only)
 * @returns {boolean}
 */
function isLocalStorageAvailable() {
  if (typeof window === 'undefined') return false;
  try {
    const testKey = '__test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get item from localStorage
 * @param {string} key - Storage key
 * @returns {string | null} Raw string value or null
 */
export function getItem(key) {
  if (!isLocalStorageAvailable()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Set item in localStorage
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 * @returns {void}
 */
export function setItem(key, value) {
  if (!isLocalStorageAvailable()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set localStorage item "${key}":`, error);
  }
}

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export function removeItem(key) {
  if (!isLocalStorageAvailable()) return false;
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove localStorage item "${key}":`, error);
    return false;
  }
}

/**
 * Clear all items from localStorage
 * @returns {boolean} Success status
 */
export function clear() {
  if (!isLocalStorageAvailable()) return false;
  try {
    window.localStorage.clear();
    return true;
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
    return false;
  }
}

/**
 * Get all keys from localStorage
 * @returns {string[]} Array of keys
 */
export function getKeys() {
  if (!isLocalStorageAvailable()) return [];
  try {
    return Object.keys(window.localStorage);
  } catch {
    return [];
  }
}

/**
 * Check if key exists in localStorage
 * @param {string} key - Storage key
 * @returns {boolean}
 */
export function hasItem(key) {
  if (!isLocalStorageAvailable()) return false;
  return window.localStorage.getItem(key) !== null;
}

/**
 * Get item with expiration support
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist or expired
 * @returns {*} Stored value or default
 */
export function getItemWithExpiry(key, defaultValue = null) {
  if (!isLocalStorageAvailable()) return defaultValue;
  try {
    const item = window.localStorage.getItem(key);
    if (!item) return defaultValue;

    const { value, expiry } = JSON.parse(item);
    if (expiry && Date.now() > expiry) {
      window.localStorage.removeItem(key);
      return defaultValue;
    }
    return value;
  } catch {
    return defaultValue;
  }
}

/**
 * Set item with expiration
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 * @param {number} ttl - Time to live in milliseconds
 * @returns {boolean} Success status
 */
export function setItemWithExpiry(key, value, ttl) {
  if (!isLocalStorageAvailable()) return false;
  try {
    const item = {
      value,
      expiry: ttl ? Date.now() + ttl : null,
    };
    window.localStorage.setItem(key, JSON.stringify(item));
    return true;
  } catch (error) {
    console.warn(`Failed to set localStorage item "${key}" with expiry:`, error);
    return false;
  }
}

export default {
  getItem,
  setItem,
  removeItem,
  clear,
  getKeys,
  hasItem,
  getItemWithExpiry,
  setItemWithExpiry,
  isAvailable: isLocalStorageAvailable,
};
